import { useMutation } from '@tanstack/react-query';
import { MAX_LABELS_PER_REQUEST, type LabelPresetCode } from '@eombor/contracts';
import { Button, Dialog, useToast } from '@eombor/ui';
import { ApiClientError } from '../../../shared/api/client.js';
import { printBlob } from '../../../shared/api/print.js';
import { t } from '../../../shared/i18n/index.js';
import { DEFAULT_LABEL_PRESET, useLabelSize } from '../model/label-size.js';
import { LABEL_FILE_NAME, downloadBlob, labelApi } from '../api/label.api.js';
import { LabelSizeFields } from './LabelSizeFields.js';

export interface LabelPrintDialogProps {
  open: boolean;
  onClose: () => void;
  /** Bosiladigan kataklar — chaqiruvchi tanlagan ro'yxat. */
  cellIds: string[];
  preset?: LabelPresetCode;
}

/**
 * KATAK etiketkasini bosish oynasi.
 *
 * Chegara (`MAX_LABELS_PER_REQUEST`) shartnomadan olinadi va tugma **oldindan**
 * o'chiriladi: 3000 ta etiketka so'ragan odam PDF ni yarim daqiqa kutib,
 * keyin xato ko'rmasligi kerak. Backend baribir o'zi ham tekshiradi.
 *
 * ASOSIY tugma — "Bosib chiqarish": PDF to'g'ridan-to'g'ri brauzerning bosma
 * oynasiga tushadi. Yuklab olish ikkinchi darajali: u faylni boshqa
 * kompyuterga olib borish yoki keyinroq bosish uchun qoladi.
 *
 * O'lcham tanlovi `LabelSizeFields` da — MAHSULOT etiketkasi ham aynan
 * o'sha tanlovdan foydalanadi (ikkalasi bitta rulondan chiqadi).
 */
export const LabelPrintDialog = ({
  open,
  onClose,
  cellIds,
  preset: initialPreset = DEFAULT_LABEL_PRESET,
}: LabelPrintDialogProps) => {
  const toast = useToast();
  const label = useLabelSize(initialPreset);

  const tooMany = cellIds.length > MAX_LABELS_PER_REQUEST;
  const empty = cellIds.length === 0;

  /**
   * So'rov ikkala tugma uchun bitta — farqi faqat natijani nima qilishda.
   * Shu sababli `mutate` ga natijani ishlatuvchi funksiya beriladi.
   */
  const print = useMutation({
    mutationFn: (use: (blob: Blob) => void) =>
      labelApi
        .printCells({ cellIds, preset: label.preset, ...label.sizeInput() })
        .then((blob) => {
          use(blob);
        }),
    onSuccess: () => {
      toast.show(t('warehouse.labelDone'), 'success');
      onClose();
    },
    onError: (error: unknown) => {
      toast.show(
        error instanceof ApiClientError ? error.message : t('warehouse.labelFailed'),
        'danger',
      );
    },
  });

  const blocked = tooMany || empty || label.sizeMissing;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('warehouse.labelTitle')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('warehouse.cancel')}
          </Button>
          <Button
            variant="secondary"
            onClick={() => print.mutate((blob) => downloadBlob(blob, LABEL_FILE_NAME))}
            loading={print.isPending}
            disabled={blocked}
          >
            {t('warehouse.labelDownload')}
          </Button>
          <Button onClick={() => print.mutate(printBlob)} loading={print.isPending} disabled={blocked}>
            {t('warehouse.labelSubmit')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <LabelSizeFields
          state={label}
          summary={t('warehouse.labelSelected', { count: cellIds.length })}
        />

        {tooMany ? (
          <p role="alert" className="text-sm text-danger">
            {t('warehouse.labelTooMany', { max: MAX_LABELS_PER_REQUEST })}
          </p>
        ) : null}

        {empty ? (
          <p role="alert" className="text-sm text-warn">
            {t('warehouse.labelEmpty')}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
};
